import React, { createContext, useState, useEffect, ReactNode } from "react";

type AccessibilityContextType = {
    lowVision?: boolean;
    isDyslexia?: boolean;
    fontSize?: number;
};

export const AccessibilityContext = createContext<AccessibilityContextType>({
    lowVision: false,
    isDyslexia: false,
    fontSize: 95,
});

type Props = {
    children: ReactNode;
};

export const AccessibilityProvider: React.FC<Props> = ({ children }) => {
    const [lowVision, setIsLowVision] = useState<boolean | undefined>();
    const [isDyslexia, setIsDyslexia] = useState<boolean | undefined>();
    const [fontSize, setFontSize] = useState(95);

    useEffect(() => {
        const onAccessibilityChange = (e: any) => {
            const { dyslexia, lowVision, fontSize } = e.detail;

            document.body.classList.toggle("dyslexia", dyslexia);
            document.body.classList.toggle("low-vision", lowVision);
            document.documentElement.style.fontSize = `${fontSize}%`;

            setIsDyslexia(dyslexia);
            setIsLowVision(lowVision);
            setFontSize(fontSize);

            const iframe = document.querySelector(".overlay-iframe") as HTMLIFrameElement | null;
            if (iframe?.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: "accessibilityChange", payload: { dyslexia, lowVision, fontSize } },
                    "*"
                );
            }
        };

        const accessibility: AccessibilityContextType = JSON.parse(
            localStorage.getItem("accessibility") ?? "{}"
        );

        setIsDyslexia(accessibility.isDyslexia);
        setIsLowVision(accessibility.lowVision);
        setFontSize(accessibility.fontSize ?? 95);

        document.body.classList.toggle("dyslexia", accessibility.isDyslexia);
        document.body.classList.toggle("low-vision", accessibility.lowVision);
        document.documentElement.style.fontSize = `${accessibility.fontSize ?? 95}%`;

        const iframe = document.querySelector(".overlay-iframe") as HTMLIFrameElement | null;
        if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                { type: "accessibilityChange", payload: accessibility },
                "*"
            );
        }

        window.addEventListener("accessibilityChange", onAccessibilityChange);
        return () => window.removeEventListener("accessibilityChange", onAccessibilityChange);
    });

    return (
        <AccessibilityContext.Provider value={{ lowVision, isDyslexia, fontSize }}>
            {children}
        </AccessibilityContext.Provider>
    );
};
